/**
 * 오디오 버스 (FR-016, research §9) — 리얼리스틱 절차 합성.
 * Web Audio API 직접 사용(라이브러리 금지). 재질별 파괴음(세라믹 파쇄 / 플라스틱 크랙 /
 * 금속 크런치 / 유리 화면 tink / CRT 폭발 / 고무 thud)을 다층 합성하고, 컨볼루션 리버브로 공간감을 준다. 마스터에
 * 컴프레서를 걸어 여러 보이스가 겹쳐도 찌그러지지 않게 하고, 충돌 위치 x로 스테레오
 * 패닝해 공간감을 준다. 비파괴 충격음(책상에 쿵)·파편 클래터도 재생. 첫 사용자
 * 입력 이후에만 재생(autoplay 준수), 음소거 토글, 피치 ±15% 변조.
 */
import type { EventBus } from '../core/EventBus';
import type { Vec3, SoundProfile, SoundSpec } from '../core/types';
import { OBJECT_CATALOG } from '../config/objects';

/** def.id → 파괴음 스펙. 카탈로그(config/objects.ts)가 소유 — 여기선 조회만(중복 매핑 제거). */
const SOUND_OF = new Map<string, SoundSpec>(OBJECT_CATALOG.map((d) => [d.id, d.sound]));

/** 금속 파샬 주파수(비조화 — 실제 금속판의 모드에 가깝게). 두꺼운 판 톤: 4개만 사용. */
const METAL_PARTIALS = [520, 838, 1273, 1735];

// --- 감각 튜닝 상수 ---
/** 메이크업 게인 — 전체 출력을 크게 밀어올린다(피크는 뒤의 소프트 클리퍼가 천장에서 잡음). */
const MAKEUP_GAIN = 1.8;
/** 비파괴 충격음 전역 스팸 방지 창(ms). */
const IMPACT_THROTTLE_MS = 60;
/** 파편 클래터음 전역 스팸 방지 창(ms). */
const DEBRIS_THROTTLE_MS = 70;
/** 재생 피치 변조 폭(±15%). */
const PITCH_SPREAD = 0.15;
/** 팬 정규화 분모(책상 반폭 기준 → ±0.75로 클램프). */
const PAN_HALF_WIDTH = 2.2;

/** 월드 x 좌표 → 스테레오 팬(-0.75..0.75). 책상 폭 기준으로 정규화. */
function panFromX(x: number): number {
  return Math.max(-0.75, Math.min(0.75, x / PAN_HALF_WIDTH));
}

/** 소프트 클리퍼 커브(tanh) — |입력|≥1은 ceil로 부드럽게 포화, 저역대는 거의 선형. */
function makeSoftClipCurve(k: number, ceil: number): Float32Array<ArrayBuffer> {
  const n = 2048;
  const curve = new Float32Array(n);
  const norm = Math.tanh(k);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1; // -1..1
    curve[i] = (ceil * Math.tanh(k * x)) / norm;
  }
  return curve;
}

export class AudioBus {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private makeup: GainNode | null = null;
  private softclip: WaveShaperNode | null = null;
  private reverb: ConvolverNode | null = null;
  private reverbSend: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  /** 실측 세라믹 파괴 샘플(freesound.org, CREDITS.md 참고) — 로드 전엔 절차 합성 폴백. */
  private ceramicSample: AudioBuffer | null = null;
  /** 실측 플라스틱 충돌 샘플(freesound CC0) — 오리/헤드셋/키보드/마우스 공용. */
  private plasticSample: AudioBuffer | null = null;
  /** 실측 맥북 슬램 샘플(freesound CC0 379924, 책상 강타) — 맥북/모니터 파괴 어택. */
  private slamSample: AudioBuffer | null = null;
  /** 음향 크기 0.0–1.0 (0 = 음소거). 마스터 게인 = volume. */
  private volume = 0.7;
  private unlocked = false;
  private primed = false;
  private activeVoices = 0;
  /**
   * 동시 보이스 상한. tone() 오실레이터까지 전부 집계하므로(과거엔 노이즈/샘플만 세어
   * 실질 상한이 더 높았다) 32로 두면 phone(glass+metal ≈ 29~33 보이스) 파괴 직후의 연쇄
   * 파괴음이 새로 무음화된다 — 전 보이스 집계 기준으로 종전 실질 워스트(≈50~70)와
   * 비슷한 천장을 유지하도록 64로 보정(폭주 방지 목적은 그대로).
   */
  private readonly maxVoices = 64;
  /** 충격음 스팸 방지 타임스탬프 (performance.now 기준, ms). */
  private lastImpactAt = 0;
  private lastDebrisAt = 0;

  constructor(bus: EventBus) {
    bus.on('object:broken', (e) => this.playBreak(e.defId, e.impactSpeed, e.position));
    bus.on('object:impact', (e) => this.playImpact(e.defId, e.relSpeed, e.position));
    bus.on('debris:impact', (e) => this.playDebris(e.relSpeed, e.position));
    bus.on('desk:destroyed', (e) => this.playDeskBreak(e.position));
    bus.on('input:throw', () => this.playWhoosh());
    bus.on('rage:sweep', () => this.playSweepWhoosh());
    bus.on('settings:changed', (s) => this.setVolume(s.volume));

    // 언락을 견고하게: 첫 제스처에 컨텍스트 생성/resume + 이후에도 suspended면 재개.
    // (한 번 지우지 않고 유지 — 이미 running이면 resume은 무비용. 탭 포커스 복귀도 처리.)
    const tryResume = (): void => this.unlock();
    for (const ev of ['pointerdown', 'pointerup', 'keydown', 'touchstart', 'touchend', 'click'] as const) {
      window.addEventListener(ev, tryResume, { passive: true });
    }
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tryResume();
    });
  }

  private ensureContext(): void {
    if (this.ctx) return;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.volume;
    // 마스터 컴프레서 — 파편 클래터/파샬이 겹쳐도 피크가 눌리며 단단한 질감 유지.
    this.compressor = this.ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -14;
    this.compressor.knee.value = 20;
    this.compressor.ratio.value = 5;
    this.compressor.attack.value = 0.003;
    this.compressor.release.value = 0.2;
    // master → compressor(글루) → makeup(레벨 상향) → softclip(소프트 클리퍼) → destination.
    // 메이크업으로 전체를 크게 밀어넣고, 소프트 클리퍼가 피크를 천장에서 부드럽게 포화시켜
    // (하드 클립/왜곡 없이) 더 크고 단단하게 들린다 — 파괴음엔 약간의 새추레이션이 어울린다.
    this.makeup = this.ctx.createGain();
    this.makeup.gain.value = MAKEUP_GAIN;
    this.softclip = this.ctx.createWaveShaper();
    this.softclip.curve = makeSoftClipCurve(1.1, 0.95); // 천장 0.95 — 하드 클립 방지
    // oversample='none': 출력을 커브 최댓값(0.95)으로 엄격히 상한(오버샘플 필터의 오버슈트 없음).
    this.softclip.oversample = 'none';
    this.master.connect(this.compressor);
    this.compressor.connect(this.makeup);
    this.makeup.connect(this.softclip);
    this.softclip.connect(this.ctx.destination);

    // 리버브(작은 사무실 공간 IR 합성) 센드 — 경쾌하게: 짧고 빠른 감쇠 + 드라이한 센드로
    // 꼬리를 짧게(웅웅거림 억제, 타격이 또렷하게 튄다).
    this.reverb = this.ctx.createConvolver();
    this.reverb.buffer = this.makeImpulse(0.6, 4.0);
    this.reverbSend = this.ctx.createGain();
    this.reverbSend.gain.value = 0.15;
    this.reverbSend.connect(this.reverb);
    this.reverb.connect(this.master);

    // 재사용 화이트 노이즈.
    const len = this.ctx.sampleRate;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buf;

    this.loadSample('ceramic_break.wav', (b) => (this.ceramicSample = b), '세라믹');
    this.loadSample('plastic_break.wav', (b) => (this.plasticSample = b), '플라스틱');
    this.loadSample('macbook_slam.wav', (b) => (this.slamSample = b), '맥북 슬램');
  }

  /** 실측 파괴 샘플 비동기 로드 — 실패해도 절차 합성 폴백이 있어 게임 진행에 지장 없음. */
  private loadSample(file: string, assign: (b: AudioBuffer) => void, label: string): void {
    const base = import.meta.env.BASE_URL || '/';
    fetch(`${base}assets/audio/${file}`)
      .then((res) => res.arrayBuffer())
      .then((buf) => this.ctx!.decodeAudioData(buf))
      .then((decoded) => assign(decoded))
      .catch((e) => console.warn(`[Desk Smash] ${label} 샘플 로드 실패 → 절차 합성 폴백`, e));
  }

  private makeImpulse(seconds: number, decay: number): AudioBuffer {
    const rate = this.ctx!.sampleRate;
    const len = Math.floor(rate * seconds);
    const buf = this.ctx!.createBuffer(2, len, rate);
    for (let ch = 0; ch < 2; ch++) {
      const data = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
      }
    }
    return buf;
  }

  unlock(): void {
    this.ensureContext();
    const ctx = this.ctx;
    if (!ctx) return;
    // iOS/Safari: 제스처 중에 무음 버퍼를 실제로 재생(한 번).
    if (!this.primed) {
      this.primed = true;
      try {
        const s = ctx.createBufferSource();
        s.buffer = ctx.createBuffer(1, 1, 22050);
        s.connect(ctx.destination);
        s.start(0);
      } catch {
        /* ignore */
      }
    }
    if (ctx.state === 'running') {
      this.markUnlocked(ctx);
      return;
    }
    // resume는 비동기 — 성공 시에만 언락 처리(정지 컨텍스트에 소리를 쏘면 유실됨).
    ctx.resume().then(() => this.markUnlocked(ctx)).catch((err) => {
      console.warn('[Desk Smash] 🔇 audioContext.resume() 거부됨:', err);
    });
  }

  private markUnlocked(ctx: AudioContext): void {
    if (ctx.state !== 'running' || this.unlocked) return;
    this.unlocked = true;
    console.info(`[Desk Smash] 🔊 audio running (volume=${this.volume})`);
  }

  /** 음향 크기 설정(0.0–1.0). 0이면 음소거. */
  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.02);
  }

  /**
   * @param ignoreVoiceCap 보이스 상한 무시 — 책상 반파음 전용. 반파는 직전에 중량 물건의
   * 파괴음(crt, 수십 보이스)이 동기적으로 먼저 스케줄되므로, 캡을 적용하면 피날레 사운드가
   * 항상 스킵된다("가장 충격적으로 크게" 사양 위반). 1회성 이벤트라 CPU 영향은 무시 가능.
   */
  private canPlay(ignoreVoiceCap = false): boolean {
    if (this.volume <= 0 || !this.unlocked) return false;
    this.ensureContext();
    // 재생 직전 suspended면 재개 시도(백그라운드 복귀 등).
    if (this.ctx && this.ctx.state !== 'running') void this.ctx.resume();
    if (!(this.ctx && this.master && this.noiseBuffer)) return false;
    return ignoreVoiceCap || this.activeVoices < this.maxVoices;
  }

  private pitch(): number {
    return 1 + (Math.random() * 2 - 1) * PITCH_SPREAD;
  }

  // --- 저수준 보이스 ---

  /** 보이스 출력 게인 → (선택) 스테레오 패너 → 마스터, (선택) 리버브 센드. */
  private route(g: GainNode, pan?: number, reverb?: number): void {
    const ctx = this.ctx!;
    let out: AudioNode = g;
    if (pan !== undefined && pan !== 0) {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      out = g.connect(p);
    }
    out.connect(this.master!);
    if (reverb) {
      const s = ctx.createGain();
      s.gain.value = reverb;
      g.connect(s).connect(this.reverbSend!);
    }
  }

  /** 필터드 노이즈 버스트. */
  private noise(opts: {
    type: BiquadFilterType;
    freq: number;
    q: number;
    peak: number;
    dur: number;
    reverb?: number;
    delay?: number;
    pan?: number;
  }): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + (opts.delay ?? 0);
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.playbackRate.value = this.pitch();
    const f = ctx.createBiquadFilter();
    f.type = opts.type;
    f.frequency.value = opts.freq;
    f.Q.value = opts.q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(opts.peak, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + opts.dur);
    src.connect(f).connect(g);
    this.route(g, opts.pan, opts.reverb);
    this.track(src);
    src.start(t);
    src.stop(t + opts.dur + 0.02);
  }

  /** 감쇠 톤(파샬/링). */
  private tone(opts: {
    freq: number;
    type: OscillatorType;
    peak: number;
    dur: number;
    slideTo?: number;
    reverb?: number;
    delay?: number;
    pan?: number;
  }): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + (opts.delay ?? 0);
    const osc = ctx.createOscillator();
    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.freq * this.pitch(), t);
    if (opts.slideTo) osc.frequency.exponentialRampToValueAtTime(opts.slideTo, t + opts.dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(opts.peak, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + opts.dur);
    osc.connect(g);
    this.route(g, opts.pan, opts.reverb);
    this.track(osc); // 오실레이터도 보이스 캡(maxVoices) 집계에 포함
    osc.start(t);
    osc.stop(t + opts.dur + 0.02);
  }

  /** 실측 샘플 재생(피치 ±15% 변조 + 팬 + 리버브 센드). 자체 엔벨로프가 있어 램프 불필요. */
  private playSample(buffer: AudioBuffer, opts: { peak: number; pan?: number; reverb?: number }): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = this.pitch();
    const g = ctx.createGain();
    g.gain.value = opts.peak;
    src.connect(g);
    this.route(g, opts.pan, opts.reverb);
    this.track(src);
    src.start();
  }

  /** 파편이 흩어지는 잔여 클릭 소나기 — 시간이 갈수록 밀도가 줄어든다. */
  private grains(opts: {
    count: number;
    spread: number;
    freqLo: number;
    freqHi: number;
    peak: number;
    pan?: number;
  }): void {
    for (let i = 0; i < opts.count; i++) {
      const frac = opts.count > 1 ? i / (opts.count - 1) : 0;
      this.noise({
        type: 'bandpass',
        freq: opts.freqLo + Math.random() * (opts.freqHi - opts.freqLo),
        q: 3,
        // 뒤로 갈수록 작아지는 파편 + 랜덤 편차.
        peak: opts.peak * (1 - 0.55 * frac) * (0.45 + Math.random() * 0.55),
        dur: 0.03 + Math.random() * 0.04,
        // frac² 커브 → 초반 밀집, 후반 성김(실제 파편 낙하 밀도).
        delay: 0.015 + opts.spread * frac * frac + Math.random() * 0.02,
        reverb: 0.2,
        pan: opts.pan,
      });
    }
  }

  // --- 비파괴 충격음 / 파편 클래터 ---

  /** 임계값 미달 충격 — 책상에 '쿵' + 재질별 플레이버. */
  private playImpact(defId: string, relSpeed: number, position: Vec3): void {
    const now = performance.now();
    if (now - this.lastImpactAt < IMPACT_THROTTLE_MS) return; // 전역 스팸 방지
    if (!this.canPlay()) return;
    this.lastImpactAt = now;
    const g = Math.min(0.5, relSpeed / 18);
    const bright = Math.min(1, relSpeed / 12); // 세게 부딪힐수록 밝게
    const pan = panFromX(position.x);
    // 공통: 저역 사인 드롭('쿵') + 밴드패스 노크.
    this.tone({ freq: 110, type: 'sine', peak: 0.9 * g, dur: 0.12, slideTo: 45, pan });
    this.noise({ type: 'bandpass', freq: 250 + 350 * bright, q: 1.2, peak: 0.7 * g, dur: 0.07, reverb: 0.12, pan });
    // 재질 플레이버.
    switch (defId) {
      case 'mug': // 세라믹 클링크
        this.tone({ freq: 2200, type: 'sine', peak: 0.1 * g, dur: 0.06, reverb: 0.2, pan });
        break;
      case 'macbook':
      case 'monitor': // 금속 탭
        this.tone({ freq: 700, type: 'triangle', peak: 0.12 * g, dur: 0.08, reverb: 0.15, pan });
        this.tone({ freq: 1150, type: 'triangle', peak: 0.08 * g, dur: 0.06, reverb: 0.15, pan });
        break;
      case 'phone': // 유리+알루미늄 슬랩 — 짧은 유리 클릭 + 희미한 금속 탭
        this.tone({ freq: 2600, type: 'sine', peak: 0.08 * g, dur: 0.05, reverb: 0.15, pan });
        this.tone({ freq: 950, type: 'triangle', peak: 0.06 * g, dur: 0.06, reverb: 0.1, pan });
        break;
      case 'penguin': // 고무 피규어 — 둔탁한 바운스, 클릭/링 성분 없음
        this.tone({ freq: 180, type: 'sine', peak: 0.2 * g, dur: 0.09, slideTo: 90, pan });
        break;
      case 'headphones': // 무광 ABS+러버 패드 — 낮고 짧은 톡(러버가 감쇠)
        this.noise({ type: 'lowpass', freq: 700, q: 0.8, peak: 0.22 * g, dur: 0.04, pan });
        break;
      default: // keyboard/mouse 등 플라스틱 톡
        this.noise({ type: 'bandpass', freq: 900, q: 2, peak: 0.3 * g, dur: 0.05, pan });
        break;
    }
  }

  /** 파편이 구르며 부딪히는 작은 클릭(강한 스로틀링). */
  private playDebris(relSpeed: number, position: Vec3): void {
    const now = performance.now();
    if (now - this.lastDebrisAt < DEBRIS_THROTTLE_MS) return; // 전역 스팸 방지
    if (!this.canPlay()) return;
    this.lastDebrisAt = now;
    this.noise({
      type: 'bandpass',
      freq: 1500 + Math.random() * 2000,
      q: 3,
      peak: Math.min(0.08, 0.02 + relSpeed / 60),
      dur: 0.03 + Math.random() * 0.02,
      pan: panFromX(position.x),
    });
  }

  // --- 재질별 파괴음 ---

  private warnedSkip = false;
  private playBreak(defId: string, impactSpeed: number, position: Vec3): void {
    if (!this.canPlay()) {
      if (!this.warnedSkip) {
        this.warnedSkip = true;
        console.warn(
          `[Desk Smash] 🔇 파괴음 스킵 — volume=${this.volume}, unlocked=${this.unlocked}, ctxState=${this.ctx?.state ?? 'null'}, voices=${this.activeVoices}/${this.maxVoices}, noiseBuf=${!!this.noiseBuffer}`,
        );
      }
      return;
    }
    const bright = Math.min(1, impactSpeed / 16); // 세게 부술수록 고역 성분 증가
    const pan = panFromX(position.x);
    const spec = SOUND_OF.get(defId) ?? { layers: ['plastic' as const] };
    // 파괴음 크기 — 맥북/모니터(slamSample)는 더 크게 + 충돌 속도(=가속도 기반 파괴 데미지)에
    // 비례해 동적으로 키운다. 임계 속도(≈8.5~9.5)에서 ≈1.4x, 세게 내려칠수록 최대 2.1x까지
    // (마스터 소프트 클리퍼가 천장에서 포화 — 하드 클립/왜곡 없이 "쿵"만 커진다).
    const gain = spec.slamSample
      ? Math.min(2.1, 0.9 + impactSpeed / 16)
      : Math.min(1, 0.45 + impactSpeed / 24);
    // 실측 플라스틱 충돌 샘플 우선(오리/헤드셋/키보드/마우스) — 미로드 시 layers 폴백.
    if (spec.plasticSample && !spec.slamSample && this.plasticSample) {
      this.playSample(this.plasticSample, { peak: 0.85 * gain, pan, reverb: 0.15 });
      return;
    }
    // 실측 맥북 슬램(책상 강타) 어택을 파괴음 앞에 덧입힘 — 아래 layers(CRT 파열)와 겹쳐
    // "맥북을 내려쳐 화면이 박살나는" 리얼한 질감. 미로드 시엔 layers만 재생.
    // 샘플 자체가 −1dB로 풀레벨이라 gain을 살짝 눌러(0.85) 과포화를 막고 CRT 크래시가 크기를 키운다.
    if (spec.slamSample && this.slamSample) {
      this.playSample(this.slamSample, { peak: 0.85 * gain, pan, reverb: 0.1 });
    }
    for (const l of spec.layers) {
      const [profile, scale] = Array.isArray(l) ? l : ([l, 1] as const);
      this.layer(profile, gain * scale, bright, pan);
    }
  }

  /**
   * 책상 반파음 — 게임에서 가장 충격적으로 큰 소리(피날레). 초저역 지진 붐 + 목재 대파열
   * 크랙 연타 + 광대역 크래시 + 스플린터 + 반쪽 슬랩이 바닥에 무너지는 2차 임팩트.
   * 피크가 천장을 치도록 크게 밀어넣는다(소프트 클리퍼가 포화 — 의도된 새추레이션).
   */
  private playDeskBreak(position: Vec3): void {
    if (!this.canPlay(true)) return; // 피날레 — 보이스 캡 무시(직전 crt 파괴음이 캡을 채움)
    const pan = panFromX(position.x);
    // (1) 초저역 지진 붐 — 길게 깔리는 압력파(2단 sine + 로우 노이즈).
    this.tone({ freq: 48, type: 'sine', peak: 1.4, dur: 0.9, slideTo: 16, pan });
    this.tone({ freq: 34, type: 'sine', peak: 1.0, dur: 1.1, slideTo: 14, delay: 0.02, pan });
    this.noise({ type: 'lowpass', freq: 160, q: 0.5, peak: 1.1, dur: 0.5, pan });
    // (2) 목재 대파열 — 저중역 우드 크랙 연타(상판이 결을 따라 쪼개짐).
    this.noise({ type: 'bandpass', freq: 350, q: 1.2, peak: 1.05, dur: 0.09, pan });
    this.noise({ type: 'bandpass', freq: 560, q: 1.0, peak: 0.95, dur: 0.12, delay: 0.03, reverb: 0.3, pan });
    this.noise({ type: 'bandpass', freq: 240, q: 1.2, peak: 0.9, dur: 0.16, delay: 0.07, reverb: 0.3, pan });
    this.noise({ type: 'bandpass', freq: 720, q: 0.8, peak: 0.8, dur: 0.1, delay: 0.12, reverb: 0.35, pan });
    // (3) 광대역 크래시 — 파열 직후 전대역이 함께 무너지는 소리.
    this.noise({ type: 'bandpass', freq: 1300, q: 0.4, peak: 0.85, dur: 0.8, reverb: 0.5, pan });
    this.noise({ type: 'highpass', freq: 2800, q: 0.5, peak: 0.6, dur: 0.7, delay: 0.02, reverb: 0.5, pan });
    // (4) 나무 스플린터 — 중저역 딱딱임이 넓게 흩어짐.
    this.grains({ count: 26, spread: 1.4, freqLo: 700, freqHi: 3200, peak: 0.3, pan });
    // (5) 반쪽 슬랩 바닥 붕괴(물리 낙하 타이밍 ≈0.45s) — 2차 저역 쿵 + 긴 잔해 트레일.
    this.tone({ freq: 44, type: 'sine', peak: 0.7, dur: 0.3, slideTo: 18, delay: 0.45, pan });
    this.noise({ type: 'lowpass', freq: 300, q: 0.6, peak: 0.6, dur: 0.35, delay: 0.45, pan });
    this.noise({ type: 'bandpass', freq: 900, q: 0.5, peak: 0.5, dur: 1.6, delay: 0.3, reverb: 0.55, pan });
  }

  private layer(profile: SoundProfile, g: number, bright: number, pan: number): void {
    const hi = 0.6 + 0.4 * bright; // 고역 성분 스케일
    switch (profile) {
      case 'ceramic': {
        // 실측 샘플(freesound.org 71897, CREDITS.md)이 로드됐으면 그걸 그대로 재생 — 절차
        // 합성으로는 실제 파쇄의 미세한 텍스처를 완전히 재현하기 어려워 레퍼런스와 "다르게"
        // 들렸다. 로드 전(초기 몇 초)에만 절차 합성 폴백을 쓴다.
        if (this.ceramicSample) {
          this.playSample(this.ceramicSample, { peak: 0.6 * g, pan, reverb: 0.25 });
          break;
        }
        // --- 폴백: 절차 합성(샘플 로드 전에만 사용) ---
        this.noise({ type: 'highpass', freq: 2200, q: 0.6, peak: (0.55 + 0.25 * bright) * g, dur: 0.24, reverb: 0.3, pan });
        this.noise({ type: 'bandpass', freq: 1200, q: 1.0, peak: 0.35 * g, dur: 0.12, reverb: 0.2, pan });
        this.tone({ freq: 140, type: 'sine', peak: 0.18 * g, dur: 0.1, slideTo: 70, pan });
        const crackTimes = [0.03, 0.16, 0.32, 0.47, 0.63, 0.92];
        for (const [i, dt] of crackTimes.entries()) {
          const decay = Math.pow(0.72, i);
          this.noise({
            type: 'highpass',
            freq: 2600 + Math.random() * 1200,
            q: 0.8,
            peak: 0.32 * decay * hi * g,
            dur: 0.05 + Math.random() * 0.03,
            delay: dt,
            reverb: 0.3,
            pan,
          });
        }
        this.grains({ count: 14, spread: 1.1, freqLo: 2000, freqHi: 4500, peak: 0.16 * hi * g, pan });
        this.tone({ freq: 10500, type: 'sine', peak: 0.045 * hi * g, dur: 1.8, reverb: 0.5, pan });
        this.tone({ freq: 14100, type: 'sine', peak: 0.03 * hi * g, dur: 1.5, reverb: 0.5, delay: 0.02, pan });
        break;
      }
      case 'glass': {
        // 경쾌한 유리 — 더 밝은 어택 + 조밀한 파편 소나기 + 짧고 산뜻한 링(꼬리 단축).
        this.noise({ type: 'highpass', freq: 4600, q: 0.6, peak: (0.46 + 0.26 * bright) * g, dur: 0.16, reverb: 0.32, pan });
        const n = 12 + Math.floor(Math.random() * 5);
        this.grains({ count: n, spread: 0.3, freqLo: 3400, freqHi: 8000, peak: 0.17 * hi * g, pan });
        this.tone({ freq: 5600, type: 'sine', peak: (0.05 + 0.05 * bright) * g, dur: 0.1, reverb: 0.36, pan });
        break;
      }
      case 'crt': {
        // 스크린 폭발 — 게임에서 가장 크고 파괴적인 소리(모니터/맥북). 유리 산산조각 +
        // 진공관 저역 붐 + 조밀한 광대역 크래시 + 2차 스웰(파편 재낙하) + 긴 파편 트레일.
        // 경쾌하게: 밝은 유리 파열 트랜지언트를 앞세우고, 무겁게 늘어지던 서브붐/크래시
        // 꼬리를 짧게 잘라 또렷하고 빠른 "쨍-와장창"으로. (책상 강타 저역은 실측 슬램 샘플이 담당.)
        // (0) 날카로운 초기 임팩트 — 유리가 터지는 순간의 트랜지언트(어택을 세고 밝게).
        this.noise({ type: 'highpass', freq: 2200, q: 0.4, peak: 0.9 * hi * g, dur: 0.045, reverb: 0.22, pan });
        this.noise({ type: 'lowpass', freq: 560, q: 0.6, peak: 0.8 * g, dur: 0.05, pan });
        // (1) 서브 붐 — 펀치는 남기되 꼬리를 짧게(늘어지는 웅웅거림 제거).
        this.tone({ freq: 58, type: 'sine', peak: 0.9 * g, dur: 0.24, slideTo: 28, pan });
        this.tone({ freq: 40, type: 'sine', peak: 0.55 * g, dur: 0.26, slideTo: 20, pan });
        this.noise({ type: 'lowpass', freq: 220, q: 0.5, peak: 0.6 * g, dur: 0.18, pan });
        // (2) 조밀한 광대역 크래시 — 튜브 파열 + 케이스 파쇄(꼬리 단축으로 산뜻하게).
        this.noise({ type: 'bandpass', freq: 1750, q: 0.4, peak: (0.72 + 0.28 * bright) * g, dur: 0.4, reverb: 0.38, pan });
        this.noise({ type: 'highpass', freq: 3600, q: 0.5, peak: (0.58 + 0.28 * bright) * hi * g, dur: 0.42, reverb: 0.38, delay: 0.02, pan });
        // (3) 유리 산산조각 — 밝고 조밀한 파편 크래클(스크린 유리가 흩어짐, 더 밝게).
        this.grains({ count: 24, spread: 1.3, freqLo: 3600, freqHi: 9500, peak: 0.28 * hi * g, pan });
        // (4) 연결 레이어 — 무음 없이 잦아드는 붕괴음(길이 대폭 단축으로 뒤끝 없이).
        this.noise({ type: 'bandpass', freq: 1400, q: 0.3, peak: 0.3 * g, dur: 0.9, reverb: 0.26, pan });
        // (5) 2차 스웰 — 파편이 다시 떨어지는 충격(더 빠르게 붙여 경쾌하게).
        this.noise({ type: 'bandpass', freq: 2100, q: 0.5, peak: 0.3 * hi * g, dur: 0.3, delay: 0.5, reverb: 0.3, pan });
        this.noise({ type: 'bandpass', freq: 1900, q: 0.5, peak: 0.18 * hi * g, dur: 0.24, delay: 0.9, reverb: 0.26, pan });
        // (6) 파편 정착 트레일 — 넓게 퍼진 유리·플라스틱 클릭(짧게).
        this.grains({ count: 14, spread: 1.1, freqLo: 3000, freqHi: 7500, peak: 0.18 * hi * g, pan });
        break;
      }
      case 'plastic':
        // 경쾌한 크랙 — 더 밝고 또렷한 2단 크랙(케이스가 탁탁 갈라짐) + 산뜻한 상단 클릭.
        this.noise({ type: 'bandpass', freq: 1850, q: 1.6, peak: (0.46 + 0.22 * bright) * g, dur: 0.05, reverb: 0.12, pan });
        this.noise({ type: 'bandpass', freq: 1500, q: 1.6, peak: 0.3 * g, dur: 0.04, delay: 0.025, reverb: 0.12, pan });
        this.noise({ type: 'highpass', freq: 3200, q: 0.7, peak: 0.14 * hi * g, dur: 0.03, pan });
        // 저역 thud를 짧게(뒤끝 없이 팝) + 잔여 파편.
        this.tone({ freq: 155, type: 'sine', peak: 0.4 * g, dur: 0.11, slideTo: 60, pan });
        this.grains({ count: 5, spread: 0.18, freqLo: 900, freqHi: 1800, peak: 0.16 * g, pan });
        break;
      case 'metal': {
        // 두꺼운 알루미늄 유니바디 — 얇은 판처럼 울리지 않고 뭉툭하게 찌그러진다(레퍼런스:
        // youtu.be/dxNQxzXn_Ug — 둔탁한 크런치가 지배적, 밝은 챙그랑거림 최소화).
        // 저역 임팩트 — 무게감의 핵심.
        this.tone({ freq: 85, type: 'sine', peak: 0.7 * g, dur: 0.26, slideTo: 38, pan });
        this.noise({ type: 'lowpass', freq: 420, q: 0.6, peak: 0.55 * g, dur: 0.13, pan });
        // 크런치 — 찌그러지는 표면(중저역 노이즈, 날카로운 고역 지양).
        this.noise({ type: 'bandpass', freq: 700 + 200 * bright, q: 0.8, peak: (0.4 + 0.12 * bright) * g, dur: 0.15, reverb: 0.22, pan });
        this.noise({ type: 'bandpass', freq: 380, q: 1.0, peak: 0.3 * g, dur: 0.2, delay: 0.03, reverb: 0.2, pan });
        // 그릿(edge) — 기존 대비 대폭 축소, 밝기 최소한만 반영(챙그랑거림 억제).
        this.noise({ type: 'highpass', freq: 2200, q: 0.8, peak: 0.08 * hi * g, dur: 0.05, reverb: 0.15, pan });
        // 비조화 파샬 4개 — 저역대로 이동 + 급감쇠(두꺼운 판은 댐핑이 커 오래 안 울린다).
        for (const [i, base] of METAL_PARTIALS.entries()) {
          const detune = 1 + (Math.random() * 2 - 1) * 0.04;
          this.tone({
            freq: base * detune * 0.55,
            type: i % 2 === 0 ? 'triangle' : 'sine',
            peak: 0.07 * Math.pow(0.55, i) * (i >= 2 ? hi : 1) * g,
            dur: 0.2 * Math.pow(0.65, i), // ≈0.2s → ≈0.03s (기존 0.8s→0.35s 대비 대폭 단축)
            reverb: 0.18,
            pan,
          });
        }
        // 크런치 그레인 — 반짝이는 유리 파편 대신 둔탁한 중저역 딱딱임.
        this.grains({ count: 6, spread: 0.16, freqLo: 260, freqHi: 850, peak: 0.16 * g, pan });
        break;
      }
      case 'rubber':
        // 밀도 있는 고무 — 둔탁한 저역 바운스. 링잉/크랙 성분 없음.
        this.tone({ freq: 95, type: 'sine', peak: 0.6 * g, dur: 0.18, slideTo: 40, pan });
        // 표면 마찰성 저역 노이즈 — 어택을 부드럽게(잔향도 최소).
        this.noise({ type: 'lowpass', freq: 480, q: 0.7, peak: 0.32 * g, dur: 0.09, reverb: 0.08, pan });
        // 살짝 늦은 2차 바운스 — 더 작고 낮게(고무가 한 번 튀는 느낌).
        this.tone({ freq: 78, type: 'sine', peak: 0.26 * g, dur: 0.12, slideTo: 36, delay: 0.09, pan });
        break;
    }
  }

  private playWhoosh(): void {
    if (!this.canPlay()) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.playbackRate.value = this.pitch();
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.Q.value = 1.2;
    f.frequency.setValueAtTime(500, t);
    f.frequency.exponentialRampToValueAtTime(2200, t + 0.18);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.18, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    src.connect(f).connect(g).connect(this.master!);
    this.track(src);
    src.start(t);
    src.stop(t + 0.24);
  }

  /** 쓸어버리기 — 팔 전체로 훑는 크고 낮은 스윕 우시(던지기 whoosh보다 길고 굵게). */
  private playSweepWhoosh(): void {
    if (!this.canPlay()) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.playbackRate.value = this.pitch();
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.Q.value = 0.9;
    f.frequency.setValueAtTime(220, t);
    f.frequency.exponentialRampToValueAtTime(1600, t + 0.28);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.5, t + 0.08);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.38);
    src.connect(f).connect(g).connect(this.master!);
    this.track(src);
    src.start(t);
    src.stop(t + 0.4);
  }

  private track(src: AudioScheduledSourceNode): void {
    this.activeVoices++;
    src.addEventListener('ended', () => {
      this.activeVoices = Math.max(0, this.activeVoices - 1);
    });
  }
}
